import { PureComponent, type ReactElement } from "react";

import { type IRsqlComparisonExpr } from "@/app-utils/rsql.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { type ListingMapEntry, type OrganismsInfo } from "@/graphql.ts";
import { CONTENT_BROWSER_GENOME_TABLE_FILTERS, type IGenomeTableFiltersType } from "@/redux/modules/content-browser.ts";
import {
  FEATURE_ANNOTATION,
  GROUP_BY_ATTRIBUTE_FEATURES,
  GROUP_BY_ATTRIBUTE_PROTCOMP,
  GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
  GROUP_BY_ATTRIBUTE_SPECIES,
  GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
  GROUP_BY_TAXONOMIC_GROUP,
  ORGANISM_TABLE_COLUMNS,
  SEQUENCING_TYPE_KEY,
} from "@/settings/settings.js";

import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import { TooltipController } from "../core/overlays/components/OverlayControllers.js";

export interface IContentBrowserChartInfoProps {
  organismsInfo?: OrganismsInfo;
  selectedEntry: ListingMapEntry;
  listing: ListingMapEntry[];
  selectedContentBrowserAttribute: IRouteNavItemProps;

  changeStatisticsFilter: (columnField: string, filter: IRsqlComparisonExpr) => void;
}

/**
 * Display the Info text block of the  ContentBrowser
 */

class ContentBrowserChartInfo extends PureComponent<IContentBrowserChartInfoProps> {
  renderSequencingTypeSection(omitSequenced?: boolean): ReactElement {
    const organismsInfo: OrganismsInfo = this.props.organismsInfo;
    const sequencedGenomesLabel = `sequenced ${pluralizeWord("genome", organismsInfo.sequenced)}`;
    const assembledGenomesLabel = `public and assembled ${pluralizeWord("genome", organismsInfo.primary)}`;
    const ngsGenomesLabel = `${pluralizeWord("genome", organismsInfo.derived)} derived from aligned NGS reads`;

    const genomeTableFilters: IGenomeTableFiltersType = CONTENT_BROWSER_GENOME_TABLE_FILTERS;

    return (
      <p>
        {omitSequenced ? null : (
          <span>
            <TooltipController content={`Show only ${sequencedGenomesLabel}`}>
              {(tooltipTriggerProps) => (
                <a
                  onClick={() =>
                    this.props.changeStatisticsFilter(
                      ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
                      genomeTableFilters.SEQUENCED_GENOMES,
                    )
                  }
                  onPointerEnter={tooltipTriggerProps.onPointerEnter}
                  onPointerLeave={tooltipTriggerProps.onPointerLeave}
                  ref={tooltipTriggerProps.ref}
                >
                  {organismsInfo.sequenced}
                </a>
              )}
            </TooltipController>{" "}
            {sequencedGenomesLabel}
            <br />
          </span>
        )}
        <TooltipController content={`Show only ${assembledGenomesLabel}`}>
          {(tooltipTriggerProps) => (
            <a
              onClick={() =>
                this.props.changeStatisticsFilter(
                  ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
                  genomeTableFilters.PUBLIC_AND_ASSEMBLED_GENOMES,
                )
              }
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
              ref={tooltipTriggerProps.ref}
            >
              {organismsInfo.primary}
            </a>
          )}
        </TooltipController>{" "}
        {assembledGenomesLabel}
        <br />
        <TooltipController content={`Show only ${ngsGenomesLabel}`}>
          {(tooltipTriggerProps) => (
            <a
              onClick={() =>
                this.props.changeStatisticsFilter(
                  ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
                  genomeTableFilters.GENOMES_ALIGNED_FROM_ALIGNED_NGS_READS,
                )
              }
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
              ref={tooltipTriggerProps.ref}
            >
              {organismsInfo.derived}
            </a>
          )}
        </TooltipController>{" "}
        {ngsGenomesLabel}
        <br />
        {this.renderReferenceGenomeSection()}
      </p>
    );
  }

  renderReferenceGenomeSection() {
    const genomeTableFilters: IGenomeTableFiltersType = CONTENT_BROWSER_GENOME_TABLE_FILTERS;
    const organismsInfo: OrganismsInfo = this.props.organismsInfo;
    const label = `${pluralizeWord("genome", organismsInfo.reference)} designated as reference ${pluralizeWord(
      "genome",
      organismsInfo.reference,
    )}`;
    const tooltip = `Show only ${label}`;

    return (
      <span>
        <TooltipController content={tooltip}>
          {(tooltipTriggerProps) => (
            <a
              onClick={() =>
                this.props.changeStatisticsFilter(
                  ORGANISM_TABLE_COLUMNS.REF_GENOME,
                  genomeTableFilters.REFERENCE_GENOME,
                )
              }
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
              ref={tooltipTriggerProps.ref}
            >
              {organismsInfo.reference}
            </a>
          )}
        </TooltipController>{" "}
        {label}
        <br />
      </span>
    );
  }

  renderBySpeciesInfo(): ReactElement {
    const accessCount: number = this.props.selectedEntry
      ? this.props.selectedEntry.value
      : this.props.organismsInfo.all;
    const listingCount: number = this.props.listing ? this.props.listing.length : 0;
    return (
      <div>
        <p>
          You have access to {accessCount}
          {this.props.selectedEntry ? ` ${this.props.selectedEntry.label} ` : " "}
          {pluralizeWord("genome", accessCount)}, {pluralizeWord("strain", accessCount)}, and{" "}
          {pluralizeWord("line", accessCount)}
          {!this.props.selectedEntry ? ` across ${listingCount} species` : ""}. This includes:
        </p>
        <br />
        {this.renderSequencingTypeSection()}
      </div>
    );
  }

  renderBySequencingTypeInfo(): ReactElement {
    const accessCount: number = this.props.selectedEntry
      ? this.props.selectedEntry.value
      : this.props.organismsInfo.all;
    const defaultAccessText = `You have access to ${accessCount} ${pluralizeWord("genome", accessCount)},
        ${pluralizeWord("strain", accessCount)}, and ${pluralizeWord("line", accessCount)}`;
    const assembliesAccessText = `You have access to ${accessCount} genome ${pluralizeWord("assembly", accessCount)}.`;
    const proteinSyntenyAccessText = `You have access to ${accessCount} protein synteny ${pluralizeWord("set", accessCount)}.`;
    const geneSyntenyAccessText = `You have access to ${accessCount} gene synteny ${pluralizeWord("set", accessCount)}.`;
    const pangenomeAccessText = `You have access to ${accessCount} pangenome ${pluralizeWord("graph", accessCount)}.`;
    const unknownAccessText = `You have access to ${accessCount} ${pluralizeWord("genome", accessCount)} without a genome type assignment.`;
    const resequencedVcfAccessText = `You have access to ${accessCount} re-sequenced ${pluralizeWord("genome", accessCount)}`;

    const selectedEntryKey: string = this.props.selectedEntry ? this.props.selectedEntry.key : null;
    switch (selectedEntryKey) {
      case SEQUENCING_TYPE_KEY.UPLOADED: //  vector genomes based on uploaded sequences
        return (
          <div>
            <p>{defaultAccessText} which are connected to uploaded sequences.</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.VIRTUAL:
        return (
          <div>
            <p>{defaultAccessText} which are virtual.</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.NOT_SEQUENCED: // not sequenced
        return (
          <div>
            <p>{defaultAccessText} which have not been sequenced.</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.RE_SEQUENCED_VCF: // re-sequenced vcf
        return (
          <div>
            <p>
              {resequencedVcfAccessText} which were imported from at least one VCF file. These genomes are defined by
              their genomic variation relative to a reference genome. Complete genomic sequences may not be available
              for these genomes.
            </p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.RE_SEQUENCED: // re-sequenced
        return (
          <div>
            <p>
              {defaultAccessText} which have been re-sequenced (genomic sequence derived from NGS reads that were mapped
              to a reference genome).
            </p>
            <p>{this.renderReferenceGenomeSection()}</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.SEQUENCED_ASSEMBLY: // sequenced assembly
        return (
          <div>
            <p>{assembliesAccessText}</p>
            <p>{this.renderReferenceGenomeSection()}</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.PROTEIN_SYNTENY:
        return (
          <div>
            <p>{proteinSyntenyAccessText}</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.PANGENOME:
        return (
          <div>
            <p>{pangenomeAccessText}</p>
          </div>
        );
      case SEQUENCING_TYPE_KEY.UNKNOWN: // unknown
        return (
          <div>
            <p>{unknownAccessText}</p>
          </div>
        );
      default:
        return (
          <div>
            <p>{defaultAccessText}. This includes:</p>
            <br />
            {this.renderSequencingTypeSection()}
          </div>
        );
    }
  }

  renderByFeatureAnnotationInfo(): ReactElement {
    const selectedEntryKey: string = this.props.selectedEntry ? this.props.selectedEntry.key : null;
    let annotationText: string;
    switch (selectedEntryKey) {
      case FEATURE_ANNOTATION.GENES:
        annotationText = " which have gene annotations";
        break;
      case FEATURE_ANNOTATION.TRANSCRIPTS:
        annotationText = " which are also annotated with transcript sequences";
        break;
      case FEATURE_ANNOTATION.PROTEINS:
        annotationText = " which are also annotated with protein sequences";
        break;
      case FEATURE_ANNOTATION.EC_COVERAGE:
        annotationText = " which have protein annotations that have E.C. coverage";
        break;
    }

    const accessCount: number = this.props.selectedEntry
      ? this.props.selectedEntry.value
      : this.props.organismsInfo.sequenced;
    return (
      <div>
        <p>
          You have access to {accessCount} sequenced {pluralizeWord("genome", accessCount)}
          {!this.props.selectedEntry ? "" : annotationText}. This includes:
        </p>
        <br />
        {this.renderSequencingTypeSection(true)}
      </div>
    );
  }

  renderByVariantAnalysisGroupInfo(): ReactElement {
    if (!this.props.selectedEntry) {
      const totalCount = this.props.organismsInfo ? this.props.organismsInfo.reference : 0;
      return (
        <div>
          <p>
            A Genome Analysis Group is a set of genomes where variants have been pre-calculated between all members. The
            variants can be analyzed and visualized relative to a reference genome.
          </p>
          <br />
          <p>
            <a
              onClick={() =>
                this.props.changeStatisticsFilter(
                  ORGANISM_TABLE_COLUMNS.REF_GENOME,
                  CONTENT_BROWSER_GENOME_TABLE_FILTERS.REFERENCE_GENOME,
                )
              }
            >
              {totalCount}
            </a>{" "}
            reference {pluralizeWord("genome", totalCount)} {totalCount === 1 ? "is" : "are"} available to you across
            all Genome Analysis Groups.
          </p>
        </div>
      );
    } else {
      const filteredCount = this.props.selectedEntry ? this.props.selectedEntry.value : 0;
      const groupName = this.props.selectedEntry.label;
      return (
        <div>
          <p>
            You have access to {filteredCount} {pluralizeWord("genome", filteredCount)} in Genome Analysis Group{" "}
            <strong>{groupName}</strong>. This includes:
          </p>
          <br />

          {this.renderSequencingTypeSection()}
        </div>
      );
    }
  }

  renderTaxonomicGroupInfo(): ReactElement {
    if (!this.props.selectedEntry) {
      return (
        <div>
          <p>
            The taxonomy column can be used to group genomes according to a third-party classification. There may be
            similarities with the NCBI taxonomy but the taxonomy column is an independent grouping system on its own.
          </p>
        </div>
      );
    } else {
      const filteredCount = this.props.selectedEntry ? this.props.selectedEntry.value : 0;
      const groupName = this.props.selectedEntry.label;
      return (
        <div>
          <p>
            You have access to {filteredCount} {pluralizeWord("genome", filteredCount)} in Taxonomic Group{" "}
            <strong>{groupName}</strong>.
          </p>
        </div>
      );
    }
  }

  render(): ReactElement {
    if (!this.props.organismsInfo) {
      return null;
    }
    switch (this.props.selectedContentBrowserAttribute.eventKey) {
      case GROUP_BY_ATTRIBUTE_SPECIES:
        return this.renderBySpeciesInfo();

      case GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE:
        return this.renderBySequencingTypeInfo();

      case GROUP_BY_ATTRIBUTE_FEATURES:
        return this.renderByFeatureAnnotationInfo();

      case GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP:
        return this.renderByVariantAnalysisGroupInfo();

      case GROUP_BY_TAXONOMIC_GROUP:
        return this.renderTaxonomicGroupInfo();

      case GROUP_BY_ATTRIBUTE_PROTCOMP:
        return <span>TBD Protein Comparisons</span>;

      default:
        return <div>GroupByAttribute ${this.props.selectedContentBrowserAttribute.eventKey} not supported</div>;
    }
  }
}

export default ContentBrowserChartInfo;
